import { Injectable } from '@angular/core';

import { Observable, defer, of } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';

import { ConsumptionData, IVouchersHttpService } from '../../http';
import { ICurrentCodeService, IVouchersService } from '../interfaces';
import {
  GiftCardModel,
  GiftCardTransformer,
  SessionsModel,
  SessionsTransformer,
  VoucherBySerialNumberModel,
  VoucherBySerialNumberTransformer,
  VoucherCancellationModel,
  VoucherConsumptionModel,
  VoucherHistoryModel,
  VoucherHistoryTransformer,
  VoucherModel,
  VoucherTransformer,
} from '../../../models';

@Injectable()
export class VouchersService implements IVouchersService {
  private lastVoucher?: VoucherModel;
  private lastGiftCard?: GiftCardModel;
  private allVouchersCache$: Observable<VoucherModel[]> | null = null;
  private rawVouchersCache: VoucherModel[] = [];
  private totalVouchers = 0;
  private pageSize = 50;

  constructor(private vouchersHttpService: IVouchersHttpService, private currentCodeService: ICurrentCodeService) {}

  async getVoucher(country: string, numCard: string): Promise<VoucherModel> {
    const voucherBack = await this.vouchersHttpService.getVoucher(country, numCard);

    const voucher = VoucherTransformer.parse(voucherBack);
    this.lastVoucher = voucher;
    this.currentCodeService.setCurrentCode(voucher.code);
    return voucher;
  }

  async getVoucherHistory(country: string, numCard: string): Promise<VoucherHistoryModel> {
    this.currentCodeService.setCurrentCode(numCard);
    const voucherHistoryBack = await this.vouchersHttpService.getVoucherHistory(country, numCard);

    const voucherHistory = VoucherHistoryTransformer.parse(voucherHistoryBack);
    return voucherHistory;
  }

  async getVoucherSessions(country: string, numCard: string): Promise<SessionsModel> {
    this.currentCodeService.setCurrentCode(numCard);
    const sessionsBack = await this.vouchersHttpService.getVoucherSessions(country, numCard);

    const sessions = SessionsTransformer.parse(sessionsBack);
    return sessions;
  }

  updateLastVoucher(nbTicketsRemaining: number): void {
    if (this.lastVoucher) {
      this.lastVoucher.numberOfSeatsRemaining = nbTicketsRemaining;
    }
  }

  async getLastVoucher(country: string, voucherCode: string): Promise<VoucherModel> {
    if (this.lastVoucher?.code === voucherCode) {
      return this.lastVoucher;
    } else {
      return this.getVoucher(country, voucherCode);
    }
  }

  async consumeVoucher(country: string, numCard: string, data: ConsumptionData): Promise<VoucherConsumptionModel> {
    return this.vouchersHttpService.consumeVoucher(country, numCard, data);
  }

  async cancelVoucherOperation(
    country: string,
    numCard: string,
    transactionNumber: number,
    salesChannel: string,
  ): Promise<VoucherCancellationModel> {
    return this.vouchersHttpService.cancelVoucherOperation(country, numCard, transactionNumber, salesChannel);
  }

  async getVoucherBySerialNumber(
    country: string,
    type: string,
    serialNumberStart: number,
    serialNumberEnd?: number,
  ): Promise<VoucherBySerialNumberModel> {
    const voucherBySerialNumberBack = await this.vouchersHttpService.getVoucherBySerialNumber(
      country,
      type,
      serialNumberStart,
      serialNumberEnd,
    );
    const voucherBySerialNumber = VoucherBySerialNumberTransformer.parse(voucherBySerialNumberBack);
    if (!serialNumberEnd) {
      this.lastVoucher = {
        ...voucherBySerialNumber.vouchers[0],
        type: voucherBySerialNumber.type,
        typeDescription: voucherBySerialNumber.typeDescription,
      };
      this.currentCodeService.setCurrentCode(this.lastVoucher.code);
    }
    return voucherBySerialNumber;
  }

  getVoucherBySerialNumberWithPagination(
    country: string,
    type: string,
    serialNumberStart: number,
    serialNumberEnd: number,
    pageIndex: number,
  ): Observable<{ data: VoucherModel[]; total: number; hasMore: boolean }> {
    const ensureCachePopulated$ = this.ensureVouchersCache(country, type, serialNumberStart, serialNumberEnd);

    return ensureCachePopulated$.pipe(
      map(() => {
        const start = pageIndex * this.pageSize;
        const end = start + this.pageSize;
        const chunkToReturn = this.rawVouchersCache.slice(start, end);
        return {
          data: chunkToReturn,
          total: this.totalVouchers,
          hasMore: end < this.totalVouchers,
        };
      }),
    );
  }

  getAllVouchersForExport(country: string, type: string, serialNumberStart: number, serialNumberEnd: number): Observable<VoucherModel[]> {
    const ensureCachePopulated$ = this.ensureVouchersCache(country, type, serialNumberStart, serialNumberEnd);

    return ensureCachePopulated$.pipe(
      map(() => {
        return this.rawVouchersCache;
      }),
    );
  }

  private ensureVouchersCache(
    country: string,
    type: string,
    serialNumberStart: number,
    serialNumberEnd: number,
  ): Observable<VoucherModel[]> {
    if (this.allVouchersCache$ && this.rawVouchersCache.length > 0) {
      return of(this.rawVouchersCache);
    }

    this.allVouchersCache$ = defer(() =>
      this.vouchersHttpService.getVoucherBySerialNumber(country, type, serialNumberStart, serialNumberEnd),
    ).pipe(
      tap((response) => {
        const voucherBySerialNumber = VoucherBySerialNumberTransformer.parse(response);
        this.rawVouchersCache = voucherBySerialNumber.vouchers.map((voucher) => ({
          ...voucher,
          type: voucherBySerialNumber.type,
          typeDescription: voucherBySerialNumber.typeDescription,
        }));
        this.totalVouchers = this.rawVouchersCache.length;
      }),
      map((response) => {
        const voucherBySerialNumber = VoucherBySerialNumberTransformer.parse(response);
        return voucherBySerialNumber.vouchers.map((voucher) => ({
          ...voucher,
          type: voucherBySerialNumber.type,
          typeDescription: voucherBySerialNumber.typeDescription,
        }));
      }),
      shareReplay(1),
    );
    return this.allVouchersCache$;
  }

  resetCache(): void {
    this.allVouchersCache$ = null;
    this.rawVouchersCache = [];
    this.totalVouchers = 0;
  }

  async getGiftCard(country: string, numCard: string, pin: number): Promise<GiftCardModel> {
    const giftCardBack = await this.vouchersHttpService.getGiftCard(country, numCard, pin);
    const giftCard = GiftCardTransformer.parse(giftCardBack);
    this.lastGiftCard = giftCard;
    return giftCard;
  }

  getLastGiftCard(code: string): GiftCardModel | null {
    if (this.lastGiftCard?.code === code) {
      return this.lastGiftCard;
    } else {
      return null;
    }
  }
}
